import {createContext, ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {UserManager, UserManagerSettings, WebStorageStateStore} from "oidc-client-ts";

import {setReloadingFlag} from "@/tools";
import LoadingScreen from "@/components/LoadingScreen";
import {useDispatch} from "react-redux";
import {setBackendError} from "@/actions";
import {concertoLocalStorage} from "@/utils/localStorage";

const UserManagerContext = createContext(undefined);

export function useUserManager(): UserManager {
    return useContext(UserManagerContext);
}

function finalClientSettings(): UserManagerSettings {
    return {...window.oidcClientSettings, userStore: new WebStorageStateStore({store: window.localStorage})};
}

async function login(userManager: UserManager): Promise<boolean> {
    const user = await userManager.getUser();
    if (user) {
        return true;
    }

    const loc = window.location;

    const path = `${loc.protocol}//${loc.host}${loc.pathname}`;

    if (path === window.oidcClientSettings.redirect_uri) {
        console.debug("Processing PCKE response");
        const user = await userManager.signinCallback(window.location.href);

        concertoLocalStorage.set("profile", user.profile);
        setReloadingFlag();
        window.location.href = concertoLocalStorage.get("beforeLoginUrl");
    } else {
        console.debug("Start PKCE sign-in");
        concertoLocalStorage.set("beforeLoginUrl", window.location.href);
        setReloadingFlag();
        await userManager.signinRedirect();
    }

    return false;
}

/**
 * Ensures that the user is logged in and the profile is available. Loading screen in displayed until then.
 *
 * The validity of the access token is not checked. It will be automatically refreshed by other code and if the refresh
 * fails, then the reload get us back here to refresh the login.
 */
export const Login = ({children}: {children: ReactNode}) => {
    const [ready, setReady] = useState(false);
    const userManager = useMemo(() => new UserManager(finalClientSettings()), []);

    const dispatch = useDispatch();

    useEffect(() => {
        login(userManager)
            .then((ready) => {
                setReady(ready);
            })
            .catch((exc) => {
                console.warn(`PKCE process failed: ${exc}`);
                dispatch(setBackendError({code: "cant_reach_heimdall"}));
            });
    }, [userManager, setReady, dispatch]);

    if (!ready) return <LoadingScreen />;

    return <UserManagerContext.Provider value={userManager}>{children}</UserManagerContext.Provider>;
};
